import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Container,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  FavoriteBorder as WishlistIcon,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import CartBadge from './CartBadge';
import UserMenu from './UserMenu';
import AnnouncementBar from './AnnouncementBar';
import MegaMenu from './MegaMenu';
import SearchBar from './SearchBar';
import { useStorefrontConfig } from '../StorefrontConfigProvider';

// Fallback navigation used when no NavMenuItem rows are configured.
const FALLBACK_NAV = [
  { id: 'men', label: 'MEN', link: '/shop/products?department=MEN', children: [] },
  { id: 'women', label: 'WOMEN', link: '/shop/products?department=WOMEN', children: [] },
  { id: 'kids', label: 'KIDS', link: '/shop/products?department=KIDS', children: [] },
  { id: 'sale', label: 'SALE', link: '/shop/products?section=sale', highlight: true, children: [] },
];

const StorefrontHeader = () => {
  const navigate = useNavigate();
  const { settings, navigation } = useStorefrontConfig();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const nav = navigation && navigation.length > 0 ? navigation : FALLBACK_NAV;
  const storeName = settings?.store_name || 'Store';

  const Logo = () => (
    <Box
      sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mr: { md: 3 } }}
      onClick={() => navigate('/shop')}
    >
      {settings?.logo ? (
        <Box component="img" src={settings.logo} alt={storeName} sx={{ height: 36 }} />
      ) : (
        <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main', letterSpacing: '-0.02em' }}>
          {storeName}
        </Typography>
      )}
    </Box>
  );

  return (
    <Box sx={{ position: 'sticky', top: 0, zIndex: 1100 }}>
      <AnnouncementBar />
      <AppBar position="static" elevation={0} sx={{ bgcolor: 'background.paper', color: 'text.primary', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Container maxWidth="xl">
          {/* Row 1: logo + search + actions */}
          <Toolbar disableGutters sx={{ gap: 2, minHeight: { xs: 56, md: 64 } }}>
            <IconButton edge="start" sx={{ display: { md: 'none' } }} onClick={() => setDrawerOpen(true)}>
              <MenuIcon />
            </IconButton>

            <Logo />

            <Box sx={{ flexGrow: 1, maxWidth: 560, display: { xs: 'none', sm: 'block' } }}>
              <SearchBar />
            </Box>

            <Box sx={{ flexGrow: { xs: 1, sm: 0 } }} />

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {settings?.enable_wishlist !== false && (
                <IconButton color="inherit" onClick={() => navigate('/shop/wishlist')} aria-label="Wishlist">
                  <WishlistIcon />
                </IconButton>
              )}
              <CartBadge />
              <UserMenu />
            </Box>
          </Toolbar>

          {/* Row 2: mega menu (desktop) */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <Divider />
            <Box sx={{ height: 48 }}>
              <MegaMenu navigation={nav} />
            </Box>
          </Box>

          {/* Mobile search row */}
          <Box sx={{ display: { xs: 'block', sm: 'none' }, pb: 1.5 }}>
            <SearchBar />
          </Box>
        </Container>
      </AppBar>

      {/* Mobile drawer nav */}
      <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 280, pt: 1 }} role="presentation">
          <Box sx={{ px: 2, py: 1 }}>
            <Logo />
          </Box>
          <Divider />
          <List>
            {nav.map((item) => {
              const hasChildren = item.children && item.children.length > 0;
              const isOpen = expanded === item.id;
              return (
                <React.Fragment key={item.id}>
                  <ListItemButton
                    onClick={() => {
                      if (hasChildren) {
                        setExpanded(isOpen ? null : item.id);
                      } else {
                        setDrawerOpen(false);
                        navigate(item.link);
                      }
                    }}
                  >
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ fontWeight: 600, color: item.highlight ? 'error.main' : 'inherit' }}
                    />
                    {hasChildren && (isOpen ? <ExpandLess /> : <ExpandMore />)}
                  </ListItemButton>
                  {hasChildren && (
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                      <List disablePadding>
                        {item.children.map((c) => (
                          <ListItemButton
                            key={c.id}
                            sx={{ pl: 4 }}
                            onClick={() => {
                              setDrawerOpen(false);
                              navigate(c.link);
                            }}
                          >
                            <ListItemText primary={c.label} />
                          </ListItemButton>
                        ))}
                      </List>
                    </Collapse>
                  )}
                </React.Fragment>
              );
            })}
          </List>
        </Box>
      </Drawer>
    </Box>
  );
};

export default StorefrontHeader;
